import * as React from "react";
import useBackend from "../../hooks/use_backend";
import {
  Box,
  Typography,
  useTheme,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import Loader from "../../components/general/loader";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";
import { fetchSiteData } from "../../scripts/api_v2/site_client";
import { addGPC, getSiteGreenPowerCertificates, removeGPC, updateGPC } from "../../scripts/api_v2/gpcs_client";
import { EditableCard } from "../general/editable_card";
import HelpedTextField from "../general/helped_text_field";
import { enqueueSnackbar } from "notistack";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

function formatDate(from, to) {
  if (!from && !to) {
    return "Always";
  }
  if (!from && to) {
    const d = new Date(to * 1000);
    return (
      "Until " +
      ("0" + d.getDate()).slice(-2) +
      "-" +
      ("0" + (d.getMonth() + 1)).slice(-2) +
      "-" +
      d.getFullYear() +
      " " +
      ("0" + d.getHours()).slice(-2) +
      ":" +
      ("0" + d.getMinutes()).slice(-2)
    );
  }
  if (from && !to) {
    const d = new Date(from * 1000);
    return (
      "Since " +
      ("0" + d.getDate()).slice(-2) +
      "-" +
      ("0" + (d.getMonth() + 1)).slice(-2) +
      "-" +
      d.getFullYear() +
      " " +
      ("0" + d.getHours()).slice(-2) +
      ":" +
      ("0" + d.getMinutes()).slice(-2)
    );
  }
  const d = new Date(from * 1000);
  const t = new Date(to * 1000);
  return (
    ("0" + d.getDate()).slice(-2) +
    "-" +
    ("0" + (d.getMonth() + 1)).slice(-2) +
    "-" +
    d.getFullYear() +
    " " +
    ("0" + d.getHours()).slice(-2) +
    ":" +
    ("0" + d.getMinutes()).slice(-2) +
    " - " +
    ("0" + t.getDate()).slice(-2) +
    "-" +
    ("0" + (t.getMonth() + 1)).slice(-2) +
    "-" +
    t.getFullYear() +
    " " +
    ("0" + t.getHours()).slice(-2) +
    ":" +
    ("0" + t.getMinutes()).slice(-2)
  );
}

function Row({ device_id, device_name, data, onEdit, onDelete }) {
  const theme = useTheme();

  return (
    <tr>
      <td>
        <b style={{ color: theme.palette.secondary.main }}>{formatDate(data.valid_from, data.valid_to)}</b>
      </td>
      <td>
        <b style={{ paddingLeft: 20 }}>{`${device_id} - ${device_name}`}</b>
      </td>
      <td>
        <b style={{ paddingLeft: 50 }}>€{data.price}</b>
      </td>
      <td>
        <IconButton size="small" onClick={() => onEdit(device_id)}>
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => onDelete(device_id)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </td>
    </tr>
  );
}

const GPCs = ({
  gpcs,
  siteData,
  isLoading,
  isEditing,
  isCommitting,
  isError,
  newGPC,
  setNewGPC,
  handleGPCChange,
  onEditGPC,
  onDeleteRequest,
  isUpdating,
}) => {
  if (isLoading || isCommitting) {
    return (
      <Box>
        <Loader sx={{ mb: 2 }} />
      </Box>
    );
  }
  if (!gpcs || isError) {
    return (
      <Box>
        <Typography variant="body" sx={{ ml: 6 }}>
          Error loading green power certificates
        </Typography>
      </Box>
    );
  }
  if (Object.entries(gpcs).length === 0 && !isEditing) {
    return (
      <Box>
        <Typography variant="body" sx={{ ml: 6 }}>
          No active certificates
        </Typography>
      </Box>
    );
  } else {
    return (
      <>
        <Box component="div" sx={{ overflow: "auto", px: 1, display: "flex" }} boxSizing>
          <table>
            <tbody>
              {Object.entries(gpcs).map((el, idx) => (
                <Row
                  key={idx}
                  device_id={el[0]}
                  device_name={siteData.devices[el[0]]?.name}
                  data={el[1]}
                  onEdit={onEditGPC}
                  onDelete={onDeleteRequest}
                />
              ))}
            </tbody>
          </table>
        </Box>
        {isEditing && (
          <Box display="flex" flexDirection="row" alignItems="center">
            <Box display="flex" minWidth="70%" mr={2}>
              <TextField
                value={newGPC.device_id}
                label="Device"
                select
                fullWidth
                disabled={isUpdating}
                sx={{ my: 2 }}
                onChange={(e) => {
                  setNewGPC((prev) => {
                    return { ...prev, device_id: e.target?.value };
                  });
                }}
              >
                {Object.entries(siteData.devices).map(([key, value]) => (
                  <MenuItem key={key} value={key}>
                    [{key}] - {value?.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box display="flex">
              <HelpedTextField
                info="For each MWh of generated solar energy, the customer may receive a premium. Enter its value or ask the customer if unknown."
                key="gpc"
                label={"Price (€)"}
                fullWidth
                value={newGPC.price}
                onChange={(e) => handleGPCChange(e.target.value)}
              />
            </Box>
          </Box>
        )}
      </>
    );
  }
};

export function GPCCard({ permissionMap, required_privilege, startCollapsed = false, isEditable = true, ...props }) {
  const backend = useBackend();
  const params = useParams();
  const [editing, setEditing] = React.useState(false);
  const [gpcCopy, setGpcCopy] = React.useState({});
  const queryClient = useQueryClient();
  const EMPTY_GPC = { device_id: "", price: 0, previous: null };
  const [newGPC, setNewGPC] = React.useState(EMPTY_GPC);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [pendingDeleteDeviceId, setPendingDeleteDeviceId] = React.useState(null);

  const { data: siteData } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id],
    queryFn: () => fetchSiteData(backend.fetch, params.site_id),
    enabled: false, // sitePage will refresh the query
  });

  const {
    data: gpcs,
    isError,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.GREEN_POWER_CERTIFICATE, backend.fetch, params.site_id],
    queryFn: () => getSiteGreenPowerCertificates(backend.fetch, params.site_id),
    refetchInterval: 30000,
  });

  function handleEditControl() {
    setGpcCopy(gpcs ? gpcs : {});
    setNewGPC(EMPTY_GPC);
    setIsUpdating(false);
    setEditing(true);
  }

  function handleEditGPC(deviceId) {
    const currentGPC = gpcs?.[deviceId];
    if (!currentGPC) {
      return enqueueSnackbar("Certificate not found", { variant: "error" });
    }
    setGpcCopy(gpcs ? gpcs : {});
    setNewGPC({
      device_id: deviceId,
      price: currentGPC.price ?? 0,
      previous: currentGPC.price ?? null,
    });
    setIsUpdating(true);
    setEditing(true);
  }

  function handleCancelConfig() {
    queryClient.setQueryData([REACT_QUERY_KEYS.GREEN_POWER_CERTIFICATE, backend.fetch, params.site_id], (prev) => {
      return gpcCopy;
    });
    setNewGPC(EMPTY_GPC);
    setIsUpdating(false);
    setEditing(false);
  }

  async function handleCommitGPC() {
    const gpc = { ...newGPC };
    if (gpc.device_id === "") {
      return enqueueSnackbar("Please select a device", { variant: "error" });
    }
    if (siteData.devices[gpc.device_id].device_class === "meter") {
      return enqueueSnackbar("Not a curtailable device", { variant: "error" });
    }
    if (gpcCopy[gpc.device_id] !== undefined) {
      gpc.previous = gpcCopy[gpc.device_id].price;
    }
    if (newGPC.price === "") {
      gpc.price = null;
      enqueueSnackbar("No price given, removing the GPC", { variant: "warning" });
    }
    if (newGPC.price < 0) {
      return enqueueSnackbar("Negative price is not possible", { variant: "error" });
    }
    if (gpc.previous === null && gpcCopy[gpc.device_id] !== undefined) {
      gpc.previous = gpcCopy[gpc.device_id].price;
    }
    try {
      commitGPC(gpc);
    } catch (err) {
      return enqueueSnackbar(err, { variant: "error" });
    }
  }

  const { mutate: commitGPC, isPending: isCommitting } = useMutation({
    mutationFn: async (gpc) => {
      setEditing(false);
      const isUpdate = gpc.previous !== null && gpc.previous !== undefined;
      if (isUpdate) {
        return updateGPC(backend.fetch, params.site_id, gpc);
      }
      return addGPC(backend.fetch, params.site_id, gpc);
    },
    onMutate: async (gpc) => {
      await queryClient.cancelQueries({
        queryKey: [REACT_QUERY_KEYS.GREEN_POWER_CERTIFICATE, backend.fetch, params.site_id],
      });
      const previousGpcs = queryClient.getQueryData([
        REACT_QUERY_KEYS.GREEN_POWER_CERTIFICATE,
        backend.fetch,
        params.site_id,
      ]);
      queryClient.setQueryData([REACT_QUERY_KEYS.GREEN_POWER_CERTIFICATE, backend.fetch, params.site_id], (prev) => {
        if (!prev) return prev;
        const updated = { ...prev };
        if (gpc.price === null || gpc.price === "") {
          delete updated[gpc.device_id];
        } else {
          updated[gpc.device_id] = { ...updated[gpc.device_id], price: gpc.price };
        }
        return updated;
      });
      return { previousGpcs };
    },
    onError: (err, variables, context) => {
      console.error(err);
      if (context?.previousGpcs) {
        queryClient.setQueryData(
          [REACT_QUERY_KEYS.GREEN_POWER_CERTIFICATE, backend.fetch, params.site_id],
          context.previousGpcs,
        );
      }
      setEditing(true);
      enqueueSnackbar("GPC save failed", { variant: "error" });
    },
    onSuccess: async (data, variables) => {
      setIsUpdating(false);
      enqueueSnackbar(
        variables?.previous !== null && variables?.previous !== undefined ? "GPC updated!" : "GPC added!",
        {
          variant: "success",
        },
      );
    },
  });

  function handleGPCChange(value) {
    setNewGPC((prev) => {
      return { ...prev, price: value };
    });
  }

  const { mutate: deleteGPCMutation, isPending: isDeleting } = useMutation({
    mutationFn: async (deviceId) => {
      return removeGPC(backend.fetch, params.site_id, deviceId);
    },
    onMutate: async (deviceId) => {
      await queryClient.cancelQueries({
        queryKey: [REACT_QUERY_KEYS.GREEN_POWER_CERTIFICATE, backend.fetch, params.site_id],
      });
      const previousGpcs = queryClient.getQueryData([
        REACT_QUERY_KEYS.GREEN_POWER_CERTIFICATE,
        backend.fetch,
        params.site_id,
      ]);
      queryClient.setQueryData([REACT_QUERY_KEYS.GREEN_POWER_CERTIFICATE, backend.fetch, params.site_id], (prev) => {
        if (!prev) return prev;
        const updated = { ...prev };
        delete updated[deviceId];
        return updated;
      });
      return { previousGpcs };
    },
    onError: async (err, variables, context) => {
      console.error(err);
      if (context?.previousGpcs) {
        queryClient.setQueryData(
          [REACT_QUERY_KEYS.GREEN_POWER_CERTIFICATE, backend.fetch, params.site_id],
          context.previousGpcs,
        );
      }
      enqueueSnackbar("GPC removal failed", { variant: "error" });
    },
    onSuccess: () => {
      enqueueSnackbar("GPC removed!", { variant: "success" });
    },
  });

  function handleDeleteRequest(deviceId) {
    setPendingDeleteDeviceId(deviceId);
    setDeleteDialogOpen(true);
  }

  function handleDeleteCancel() {
    setDeleteDialogOpen(false);
    setPendingDeleteDeviceId(null);
  }

  function handleDeleteConfirm() {
    if (!pendingDeleteDeviceId) return;
    deleteGPCMutation(pendingDeleteDeviceId);
    setDeleteDialogOpen(false);
    setPendingDeleteDeviceId(null);
  }

  return (
    <EditableCard
      startCollapsed={startCollapsed}
      sx={props.sx}
      editable={isEditable}
      editActionIcon={<AddIcon />}
      title={"Green power certificates"}
      width={"100%"}
      editing={editing}
      handleEdit={handleEditControl}
      handleSave={handleCommitGPC}
      handleCancel={handleCancelConfig}
      permissionMap={permissionMap}
      required_privilege={required_privilege}
    >
      <GPCs
        gpcs={gpcs}
        siteData={siteData}
        isLoading={isLoading}
        isCommitting={isCommitting || isDeleting}
        isError={isError}
        isEditing={editing}
        newGPC={newGPC}
        setNewGPC={setNewGPC}
        handleGPCChange={handleGPCChange}
        onEditGPC={handleEditGPC}
        onDeleteRequest={handleDeleteRequest}
        isUpdating={isUpdating}
      />
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-gpc-dialog-title"
        aria-describedby="delete-gpc-dialog-description"
      >
        <DialogTitle id="delete-gpc-dialog-title">Delete certificate?</DialogTitle>
        <DialogContent>
          <Typography id="delete-gpc-dialog-description">
            Are you sure you want to delete this green power certificate?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </EditableCard>
  );
}
